import type { FC, ReactNode } from "react";
import { Fragment } from "react";
import { useMediaQuery } from "..";
import "./informationList.scss";

type InformationListSize = "sm-col" | "full-col";

type MediaScreen = {
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
};

interface Props<TData> {
  data: TData[];
  size?: InformationListSize;
  getItemKey?: (line: TData) => string | number;
  getItemClassName?: (line: TData) => string | undefined;
  getItemHidden?: (line: TData) => boolean | undefined | MediaScreen;
  renderContent: (line: TData) => ReactNode;
}

function InformationList<TData>(props: Props<TData>) {
  const {
    data,
    size = "sm-col",
    getItemKey,
    getItemClassName,
    getItemHidden,
    renderContent,
  } = props;

  return (
    <div className={`list ${size}`}>
      {data.map((line, index) => {
        const key = getItemKey?.(line) ?? index;
        const className = getItemClassName?.(line) ?? "";
        const hidden = getItemHidden?.(line) ?? false;
        const content = renderContent(line);

        return (
          <Fragment key={key}>
            {typeof hidden === "boolean" ? (
              hidden ? (
                <></>
              ) : (
                <div className={`item ${className}`}>{content}</div>
              )
            ) : (
              <Item className={className} content={content} hidden={hidden} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

type ItemProps<T> = {
  className: string;
  content: ReturnType<Props<T>["renderContent"]>;
  hidden: MediaScreen;
};

function Item<T>(props: ItemProps<T>) {
  const { hidden, className, content } = props;
  const smBreakPoint = useMediaQuery("(max-width: 500px)", !("sm" in hidden));

  if (smBreakPoint && hidden.sm) {
    return null;
  }

  return <div className={`item ${className}`}>{content}</div>;
}

export default InformationList;

const renderContent = (el: ReactNode) => {
  return typeof el === "string" ? <p className="w-100">{el}</p> : el;
};

type LeftRightContentProps = {
  left: ReactNode;
  right: ReactNode;
  smColumn?: boolean;
  className?: string;
};

export const LeftRightContent: FC<LeftRightContentProps> = (props) => {
  const { left, right, smColumn = true, className } = props;

  return (
    <div className={`horizontal-content ${smColumn ? "smColumn" : ""} ${className}`}>
      {renderContent(left)}
      {renderContent(right)}
    </div>
  );
};

type ColumnContentProps = {
  label: ReactNode;
  content: ReactNode;
};

export const ColumnContent: FC<ColumnContentProps> = (props) => {
  const { content, label } = props;

  return (
    <div className="vertical-content">
      {renderContent(label)}
      {renderContent(content)}
    </div>
  );
};

type ListContentProps<TData> = {
  label: ReactNode;
  data: TData[];
  getItemHidden?: (item: TData) => boolean;
  renderItemContent: (item: TData) => ReactNode;
};

export function ListContent<TData>(props: ListContentProps<TData>) {
  const { label, data, getItemHidden, renderItemContent } = props;

  return (
    <Fragment>
      {renderContent(label)}
      <div className="InputWraper">
        <div className="wraper">
          {data.map((item, index) => {
            const hidden = getItemHidden?.(item) ?? false;
            const content = renderItemContent(item);

            return (
              <Fragment key={index}>
                {hidden ? <></> : <div className="py-2 px-3">{renderContent(content)}</div>}
              </Fragment>
            );
          })}
        </div>
      </div>
    </Fragment>
  );
}
